import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import {
    Table,
    Input,
    Button,
    Space,
    Popconfirm,
    Typography,
    message,
    Card,
    Row,
    Col,
    Dropdown,
    Modal,
    Breadcrumb
} from 'antd';
import {
    SearchOutlined,
    ExportOutlined,
    ImportOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    UndoOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const FinancialArticlesPage = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState('ascend');

    // Состояния для добавления и редактирования
    const [isAdding, setIsAdding] = useState(false);
    const [newArticleName, setNewArticleName] = useState('');
    const [editingArticleId, setEditingArticleId] = useState(null);
    const [editName, setEditName] = useState('');

    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchArticles();
    }, [pagination.current, pagination.pageSize, sortField, sortOrder]);

    const fetchArticles = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const params = {
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchText,
                sortField,
                sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc'
            };
            const response = await axios.get('/api/financial-articles', { 
                params, 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            setArticles(response.data.data || []);
            setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
        } catch (error) {
            message.error('Ошибка при загрузке финансовых статей');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchArticles();
    };

    const handleAddArticle = async () => {
        if (!newArticleName.trim()) return message.warning('Название обязательно');

        const token = localStorage.getItem('token');
        try {
            await axios.post('/api/financial-articles', { name: newArticleName }, { headers: { 'Authorization': `Bearer ${token}` } });
            setNewArticleName('');
            setIsAdding(false);
            message.success('Статья добавлена');
            fetchArticles();
        } catch (error) {
            message.error('Ошибка при добавлении статьи');
        }
    };

    const handleDoubleClick = (article) => {
        setEditingArticleId(article.id);
        setEditName(article.name);
    };

    const saveEditedArticle = async (articleId) => {
        if (!editName.trim()) return message.warning('Название не может быть пустым');
        const token = localStorage.getItem('token');
        try {
            await axios.put(`/api/financial-articles/${articleId}`, { name: editName.trim() }, { headers: { 'Authorization': `Bearer ${token}` } });
            setEditingArticleId(null);
            message.success('Статья обновлена');
            fetchArticles();
        } catch (error) {
            message.error('Ошибка при сохранении');
        }
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem('token');
        try {
            await axios.delete(`/api/financial-articles/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            message.success('Статья удалена');
            fetchArticles();
        } catch (error) {
            message.error('Ошибка при удалении');
        }
    };

    const handleTableChange = (pag, filters, sorter) => {
        setPagination(prev => ({ ...prev, current: pag.current, pageSize: pag.pageSize }));
        if (sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order);
        }
    };

    // Экспорт и Импорт
    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/financial-articles?limit=10000', { headers: { 'Authorization': `Bearer ${token}` } });
            const dataToExport = response.data.data.map(item => ({
                'ID': item.id,
                'Название': item.name,
                'Удалить': 'Нет'
            }));
            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Статьи");
            XLSX.writeFile(wb, 'финансовые_статьи.xlsx');
            message.success('Файл сформирован');
        } catch (error) {
            message.error('Ошибка при экспорте');
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Modal.confirm({
            title: 'Подтверждение импорта',
            content: 'Вы уверены? Существующие записи будут обновлены, а помеченные на удаление - удалены.',
            onOk: async () => {
                try {
                    const data = await readExcelFile(file);
                    const token = localStorage.getItem('token');
                    await axios.post('/api/financial-articles/import', data, { headers: { 'Authorization': `Bearer ${token}` } });
                    message.success('Данные импортированы');
                    fetchArticles();
                } catch (error) {
                    message.error('Ошибка при импорте');
                }
            }
        });
        e.target.value = '';
    };

    const readExcelFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
                resolve(jsonData.map(row => ({
                    id: row['ID'],
                    name: row['Название'],
                    delete_record: row['Удалить'] === 'Да' || row['Удалить'] === 1
                })));
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: sortField === 'name' ? sortOrder : null,
            render: (text, record) => {
                if (editingArticleId === record.id) {
                    return (
                        <Input 
                            value={editName} 
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={() => saveEditedArticle(record.id)}
                            onPressEnter={() => saveEditedArticle(record.id)}
                            autoFocus 
                        />
                    );
                }
                return (
                    <div 
                        onDoubleClick={() => handleDoubleClick(record)} 
                        style={{ cursor: 'pointer', minHeight: '24px' }}
                    >
                        {text}
                    </div>
                );
            }
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 150,
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<EditOutlined style={{color: '#1890ff'}} />} 
                        onClick={() => handleDoubleClick(record)}
                    />
                    <Popconfirm title="Удалить статью?" onConfirm={() => handleDelete(record.id)}>
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const getContextMenu = (record) => [
        {
            key: 'edit',
            label: 'Редактировать',
            icon: <EditOutlined />,
            onClick: () => handleDoubleClick(record)
        },
        {
            key: 'delete',
            label: 'Удалить',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDelete(record.id)
        }
    ];

    return (
        <Layout>
            <div style={{ padding: '24px' }}>
                <Card bordered={false}>
                    <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
                        <Col flex="auto">
                            <Typography.Title level={3} style={{ margin: 0 }}>
                                Финансовые статьи
                            </Typography.Title>
                        </Col>
                        <Col>
                            <Space>
                                <Button icon={<ExportOutlined />} onClick={handleExport}>Экспорт</Button>
                                <Button icon={<ImportOutlined />} onClick={() => fileInputRef.current.click()}>Импорт</Button>
                                <input type="file" ref={fileInputRef} onChange={handleImport} accept=".xlsx, .xls" style={{ display: 'none' }} />
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAdding(true)}>
                                    Добавить статью
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <div style={{ marginBottom: 16 }}>
                        <Input.Search
                            placeholder="Поиск по названию"
                            allowClear
                            enterButton="Поиск"
                            size="large"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={handleSearch}
                            style={{ maxWidth: 400 }}
                        />
                    </div>

                    {isAdding && (
                        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
                            <Space style={{ width: '100%' }}>
                                <Input
                                    placeholder="Название новой статьи"
                                    value={newArticleName}
                                    onChange={(e) => setNewArticleName(e.target.value)}
                                    style={{ width: 400 }}
                                    autoFocus
                                />
                                <Button type="primary" onClick={handleAddArticle}>Сохранить</Button>
                                <Button onClick={() => { setIsAdding(false); setNewArticleName(''); }}>Отмена</Button>
                            </Space>
                        </Card>
                    )}

                    <Table
                        columns={columns}
                        dataSource={articles}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '20', '50'],
                            showTotal: (total) => `Всего: ${total}`
                        }}
                        onChange={handleTableChange}
                        onRow={(record) => ({
                            onContextMenu: (event) => {
                                event.preventDefault();
                                // Здесь можно добавить логику для показа Dropdown вручную, 
                                // но antd Dropdown удобнее использовать как обертку.
                            },
                        })}
                        // Реализация контекстного меню через обертку рендера строк
                        components={{
                            body: {
                                row: (props) => {
                                    const record = articles.find(a => a.id === props['data-row-key']);
                                    if (!record) return <tr {...props} />;
                                    return (
                                        <Dropdown menu={{ items: getContextMenu(record) }} trigger={['contextMenu']}>
                                            <tr {...props} />
                                        </Dropdown>
                                    );
                                }
                            }
                        }}
                    />
                </Card>
            </div>
        </Layout>
    );
};

export default FinancialArticlesPage;